import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { MSTeamsWorkflows, PresenceMonitorState, PresenceUpdateReason } from '../interfaces/msteams-presence-data';
import { WorkflowService } from './workflow.service';
import { AdminService } from './admin.service';
import { MiCollabService } from './micollab.service';
import * as _ from 'lodash';
import { Account, WorkerPostRequest } from '@mitel/cloudlink-sdk';
import { shareReplay, tap } from 'rxjs/operators';
import { StatusString } from '../interfaces/integrations';
import { StatusType } from '../constants/statusType.enum';
import { Util } from '../utils/Util';
import { AccountsService } from './accounts.service';
import { ConfirmationDialogComponent } from '../../shared/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { ConfirmationDialogs } from '../../shared/components/dialogs/confirmation-dialog/confirmation-dialogs.enum';
import { MatDialog } from '@angular/material/dialog';
import LRUCache from 'lru-cache';
import { PwaService } from './pwa.service';
import { CLDSettings } from './mivb-account-settings.service';
import { FeatureToggleService } from './feature-toggle.service';
import { FeatureFlags } from '../interfaces/feature-flags';
import { SystemManagerService } from './system-manager.service';

const logContext = "MSTeamsSharedService";

@Injectable({
  providedIn: 'root'
})
export class MSTeamsSharedService {

  private monitorList: string[] = [];

  subscription: Subscription = new Subscription();
  public monitorWorkflowComplete = new BehaviorSubject<any>(undefined);
  private monitorWorkflowData: any = undefined;
  private monitorWorkflowDataObservable;
  private monitorWorkflowDataSubscription = new Subscription();
  private monitorWorkflowCompleteSubscription = new Subscription();
  private monitorWorkflowWorkerId: string = '';
  private msteamsToCLPresenceStatusStringAccount: Account;
  private msteamsToCLPresenceStatusString: StatusString = null; // undefined is a proper state
  private msteamsToCLPresenceStatusStringObservable;
  private msteamsToCLPresenceStatusStringSubscription = new Subscription();
  private clientSecretConnectorData: any = undefined;
  private clientSecretExpiry: number = undefined;

  private siteId: string = undefined;
  private isMiCollabConfigured: any = undefined;
  private hasMSTeamsIntegration: boolean = undefined;
  msTeamsIntegrationErrorStr: any;
  raceTimeoutId;

  //Change all the manual cache implementations above ^^ into
  // a cache like implemented below. It is a cleaner implementation.
  // Take the opportunity to do this when the scaling MS Teams behaviour is added 
  // because it will require a factor and re-test for both the cache and new behaviour
  private upgradePendingCache = new LRUCache<string, Promise<any>>({
		ttl: 60 * 5 * 1000,  // ms (cache item expires in 5 min)
		max: 10          // and, oldest item in cache is bumped if more than this items in cache
		// https://github.com/isaacs/node-lru-cache#readme
	}); // cache by account ID


  private cldSettingsCache = new LRUCache<string, Promise<CLDSettings | undefined>>({
    ttl: 60 * 5 * 1000,  // ms (cache item expires in 5 min)
    max: 10          // and, oldest item in cache is bumped if more than this items in cache
    // https://github.com/isaacs/node-lru-cache#readme
  }); // cache by account ID

  constructor(
    private workflowService: WorkflowService,
    private adminService: AdminService,
    private accountService: AccountsService,
    public dialog: MatDialog,
    private micollabService: MiCollabService,
    private systemManagerService: SystemManagerService,
    private featureToggleService: FeatureToggleService,
    private pwaService: PwaService) 
  {
    this.subscription.add(this.workflowService.workflowExecuteCompleted$.subscribe(wfExecuteResult => {
      console.log(`${logContext}.workflowExecuteCompleted received `, wfExecuteResult);
      if (wfExecuteResult && wfExecuteResult.data) 
      {
        if ( this.monitorWorkflowWorkerId == wfExecuteResult.workerId )
        {
          this.monitorWorkflowData = this.extractDataFromOutputs(wfExecuteResult.data);
          console.log(`${logContext}.workflowExecuteCompleted monitorWorkflowComplete.next `, this.monitorWorkflowData);
          this.monitorWorkflowComplete.next(this.monitorWorkflowData);
        }
      }
    }));

    this.subscription.add(this.accountService.currentAccountChanged.subscribe(()=> {
      this.resetAccountMSTeamsData();
    }));
  }

  // this is being handled differently than the other data in this service because this
  // data will most likely never change in the life of the Account
  resetAccountMSTeamsData()
  {
    this.siteId = undefined;
    this.isMiCollabConfigured = undefined;
    this.hasMSTeamsIntegration = undefined;
    this.msTeamsIntegrationErrorStr = undefined;
    this.clearClientSecretCache();
  }

  clearClientSecretCache() {
    this.clientSecretConnectorData = undefined;
    this.clientSecretExpiry = undefined;
  }

  clearAllMSTeamsToCLPresenceCache()
  {
    this.clearMSTeamsToCLPresenceStatusString();
    this.clearMonitorWorkflowStatus();
  }

  clearMonitorWorkflowStatus() 
  {
    this.monitorWorkflowData = undefined;
    this.monitorWorkflowDataObservable = undefined;
    this.monitorWorkflowDataSubscription.unsubscribe();
    this.monitorWorkflowDataSubscription = new Subscription();
    this.monitorWorkflowCompleteSubscription.unsubscribe();
    this.monitorWorkflowCompleteSubscription = new Subscription();
    this.monitorWorkflowWorkerId = '';
    this.monitorWorkflowComplete.next(undefined);
  }

  clearMSTeamsToCLPresenceStatusString()
  {
    this.msteamsToCLPresenceStatusString = null;
    this.msteamsToCLPresenceStatusStringObservable = undefined;
    this.msteamsToCLPresenceStatusStringSubscription.unsubscribe();
    this.msteamsToCLPresenceStatusStringSubscription = new Subscription();
    this.msteamsToCLPresenceStatusStringAccount = undefined;
    this.hasMSTeamsIntegration = undefined;
    this.msTeamsIntegrationErrorStr = undefined;
  }

  getMonitorWorkflowStatus(
    workflowId: string, 
    monitorList: string[], 
    msConnectorId: string, 
    reset: boolean = false, 
    presenceReceiverWorkerId: string = undefined): Promise<any> 
  {
    return new Promise<any>((resolve, reject) => {
      if ((this.monitorWorkflowData != undefined) && _.isEqual(monitorList, this.monitorList)) 
      {
        return resolve(this.monitorWorkflowData);
      }
      else 
      {
        if (!_.isEqual(monitorList, this.monitorList)) 
        {
          this.clearMonitorWorkflowStatus(); // need to request again
        }
        this.monitorList = monitorList;

        // no cache, get a fresh list
        if (this.monitorWorkflowDataObservable == undefined) 
        {
          this.monitorWorkflowDataObservable = this.getMonitorWorkflowDataObservable(workflowId, monitorList, msConnectorId, reset, presenceReceiverWorkerId);
        }
        this.monitorWorkflowDataSubscription.add(this.monitorWorkflowDataObservable.subscribe( async monitorData => {
          console.log(`${logContext}.getMonitorWorkflowDataObservable.subscribe BEFORE resolve result=`, monitorData)
          if ( monitorData && monitorData.error )
          {
            // detected an error
            console.error(`${logContext}.getMonitorWorkflowDataObservable.subscribe  error=`, monitorData.error)
            this.raceTimeoutId = undefined;
            this.monitorWorkflowData = {};
            this.clearMonitorWorkflowStatus();
            this.workflowService.clearWorkflowSubscriptionInProgress();
            return reject(monitorData.error);
          }
          try
          {
            // this WF is now not returning a value sometimes
            // there was a change and things that use to work are now causing the UI to spin forever waiting 
            // for a result. So I put in a 60sec timeout 
            let timeOutPromise = this.rejectAfterWait(60000);
            let data = await Promise.race([timeOutPromise, Promise.resolve(monitorData)]);
            // clear the timeout so it will not fire later
            clearTimeout(this.raceTimeoutId);
            this.raceTimeoutId = undefined;
            console.log(`${logContext}.getMonitorWorkflowDataObservable.subscribe AFTER resolve result=`, data)
            this.monitorWorkflowData = data;
            return resolve(this.monitorWorkflowData);
          }
          catch(e)
          {
            // this is a timeout of the WF execution
            // return no data so the caller can stop waiting
            if ( this.raceTimeoutId == undefined )
            {
              // might have been a "race" situation but the data was receive before timeout
              return resolve(this.monitorWorkflowData);
            }
            console.error(`${logContext}.getMonitorWorkflowDataObservable.subscribe TIMEOUT of resolve error=`, e)
            this.raceTimeoutId = undefined;
            this.monitorWorkflowData = {};
            this.clearMonitorWorkflowStatus();
            this.workflowService.clearWorkflowSubscriptionInProgress();
            return resolve(this.monitorWorkflowData);
          }
        }));
      }
    });
  }

  // throws an error if the timeout is triggered
  // this is used to stop waiting for a result from the WF
  rejectAfterWait(ms) {
    return new Promise((_, reject) => {
       this.raceTimeoutId = setTimeout(() => reject(new Error('timeout triggered')), ms);
    });
  }

  getMonitorWorkflowDataObservable(
    workflowId: string, 
    monitorList: string[], 
    msConnectorId: string, 
    reset: boolean = false, 
    presenceReceiverWorkerId: string = undefined): Observable<any> 
  {
    return Observable.create(async (observer) => {
      let reqBody: WorkerPostRequest = {
        inputs: { 
          CLToMSTeamConnector: msConnectorId,
          ConnectionIds: monitorList, 
          PresenceReceiverWorkerId: presenceReceiverWorkerId,
          Reason: (reset == true ? PresenceUpdateReason.RESET_SUBSCRIPTIONS : undefined)
        }
      }

      return this.workflowService.executeWorkflow(workflowId, reqBody).then(async result => {
        // the data MIGHT be returned immediately, but I have not seen it happen
        if (result && (Array.isArray(result))) 
        {
          if (result.length == 1) 
          {
            this.monitorWorkflowWorkerId = result[0];
            console.log(`${logContext}.getMonitorWorkflowDataObservable next the promise`);
            observer.next(this.waitForValidatorWorkflowHandled());
          }
          else 
          {
            this.monitorWorkflowData = this.extractDataFromOutputs(result);
            this.monitorWorkflowComplete.next(this.monitorWorkflowData);
            console.log(`${logContext}.getMonitorWorkflowDataObservable next data`, this.monitorWorkflowData);
            return observer.next(this.monitorWorkflowData);
          }
        }
        else 
        {
          return observer.next({});
        }

      })
      .catch(err => {
        console.error(`${logContext}.getMonitorWorkflowDataObservable execute of the validator WF failed, error=`, err)
        return observer.next({error: err}); // having an issue with losing the error
      })
    })
    .pipe(
      tap(() => { console.debug(`${logContext}.getMonitorWorkflowDataObservable method executed`) }),
      shareReplay()
    );
  }

  waitForValidatorWorkflowHandled(): Promise<any> 
  {
    return new Promise((resolve, reject) => 
    {
      this.monitorWorkflowCompleteSubscription.add(this.monitorWorkflowComplete.subscribe(data => {
        console.log(`${logContext}.monitorWorkflowComplete.subscribe data received=`, data);
        // only resolve once the external url request is handled
        if (this.monitorWorkflowComplete.value != undefined)
        {
          console.log(`${logContext}.monitorWorkflowComplete.subscribe data received AND returned=`, data);
          resolve(data);
        }
      }));
    })
  }

  // sample of output
  // {
  //   "Success":true,
  //   "Result":
  //   {
  //     "TotalUsers":10,
  //     "ConnectionsRequired":1,
  //     "Connections":
  //     [
  //         {
  //             "ConnectionId":"335ba188-2eaa-4868-a43d-504c62021708",
  //             "Connected":true,
  //             "SubscriptionCreated":false,
  //              "SubscribedUsersCount": 5
  //         },
  //         {
  //             "ConnectionId":"ccea00be-8a2d-491a-a050-7242184b34ce",
  //             "Connected":true,
  //             "SubscriptionCreated":true
  //         },
  //         {
  //           "ConnectionId": "25435435",
  //           "Connected": false,
  //           "ErrorCode": "500",
  //           "ErrorDescription": "Graph API Error"
  //         }
  //     ]
  //   }
  // }


  extractDataFromOutputs(outputs) 
  {
    console.log(`${logContext} OUTPUT from workflow api `, outputs);
    let validationContent: any = {};
    let realValue = (outputs[0].valueType == "Object" ? outputs[0].value : undefined); // need to double check this
    if (realValue == undefined) 
    {
      return validationContent;
    }

    let valObj = JSON.parse(realValue);
    if (valObj) 
    {
      // number of cloudlink users
      validationContent.cloudlinkUserCount = valObj.Result?.TotalUsers ?? 0;
      validationContent.requiredConnectorIds = valObj.Result?.ConnectionsRequired ?? 0;
      validationContent.totalSubscribedUsers = valObj.Result?.TotalSubscribedUsersCount ?? 0;

      // here I will attempt to disciper this output when it comes to 
      // the connector status
      // STILL a work in progress
      validationContent.connectorIdStatus = [];
      validationContent.removedConnectors = [];

      if (valObj.Result && valObj.Result.Connections) 
      {
        let connections = valObj.Result.Connections;
        for (var i = 0; i < connections.length; i++) 
        {
          if (connections[i].Connected == true) 
          {
            let status = connections[i].SubscriptionCreated == true ? PresenceMonitorState.SUBSCRIBED : PresenceMonitorState.AUTHORIZED

            let connectorData = {
              connectionId: connections[i].ConnectionId,
              status: status,
              userCount: connections[i].SubscribedUsersCount
            }
            validationContent.connectorIdStatus.push(connectorData);
          }
          else // the connector is not connected for some reason, lets try to figure it out
          {
            if (connections[i].ErrorCode == "404") // does not exist
            {
              validationContent.removedConnectors.push(connections[i].ConnectionId);
            }
            else if (connections[i].ErrorCode == "401" ) 
            {
              let connectorData = {
                connectionId: connections[i].ConnectionId,
                status: PresenceMonitorState.NOT_AUTHORIZED
              }
              if ( connections[i].ErrorDescription.indexOf("Invalid Connection") != -1 )
                connectorData.status = PresenceMonitorState.INVALID;
              
              
              validationContent.connectorIdStatus.push(connectorData);
            }
            else if (connections[i].ErrorCode == "500") 
            {
              let connectorData = {
                connectionId: connections[i].ConnectionId,
                status: PresenceMonitorState.NOT_AUTHORIZED
              }
              validationContent.connectorIdStatus.push(connectorData);
            }
            else if (connections[i].ErrorCode == "403" || connections[i].ErrorCode == "503") // translate to unknown
            {
              let connectorData = {
                connectionId: connections[i].ConnectionId,
                status: PresenceMonitorState.UNKNOWN
              }
              validationContent.connectorIdStatus.push(connectorData);
            }
            else // unknown... is this required?
            {
              let connectorData = {
                connectionId: connections[i].ConnectionId,
                status: PresenceMonitorState.UNKNOWN
              }
              validationContent.connectorIdStatus.push(connectorData);
            }
          }
        }
      }
    }
    return validationContent;
  }

  // needed by both the subintegration and the parent integration
  getMSteamsToCLPresenceStatusString(account: Account): Promise<any> 
  {
    return new Promise<any>((resolve, reject) => {
      console.log("Here are some values", this.msteamsToCLPresenceStatusString, this.msteamsToCLPresenceStatusStringAccount)
      if ( (this.msteamsToCLPresenceStatusString != null) && 
          (this.msteamsToCLPresenceStatusStringAccount && (this.msteamsToCLPresenceStatusStringAccount.accountId == account.accountId)) )
      {
        return resolve(this.msteamsToCLPresenceStatusString);
      }
      else 
      {
        if ( this.msteamsToCLPresenceStatusStringAccount && (this.msteamsToCLPresenceStatusStringAccount.accountId != account.accountId) ) 
        {
          this.clearMSTeamsToCLPresenceStatusString(); // need to request again
        }
        this.msteamsToCLPresenceStatusStringAccount = account;

        // no cache, get a fresh list
        if (this.msteamsToCLPresenceStatusStringObservable == undefined) 
        {
          this.msteamsToCLPresenceStatusStringObservable = this.getMSteamsToCLPresenceStatusStringObservable(account);
        }
        this.msteamsToCLPresenceStatusStringSubscription.add(this.msteamsToCLPresenceStatusStringObservable.subscribe(statusString => {
          this.msteamsToCLPresenceStatusString = statusString;
          return resolve(statusString);
        }));
      }
    });
  }

  getMSteamsToCLPresenceStatusStringObservable(account: Account): Observable<any> 
  {
    return Observable.create(async (observer) => {

      return this.getStatusString(account).then(async result => {
            observer.next(result);
      })
    })
    .pipe(
      tap(() => { console.debug(`${logContext}.getMSteamsToCLPresenceStatusStringObservable method executed`) }),
      shareReplay()
    );
  }

  getStatusString(account: Account): Promise<StatusString>
	{
		let integStatus: StatusString = {
			status: 'integrations.MSTeamsToCLPresence.not-setup-error', 
			type: StatusType.caution
		}

		return this.adminService.getAccountTagsNoFrills(account.accountId).then( async tags => {
			// must have an onboarded MiVB and a MiCollab that supports MS Teams integration
      let hasMiVB = false;
      let cldSettings = await this.getCLDSettingsForAccount(account);
      if (cldSettings) {
        hasMiVB = true;
      } else {
        hasMiVB = Util.doesAccountHaveMiVoiceBusinessOnboarded(tags);

        if (hasMiVB && !this.siteId) {
          try {
            this.siteId = await this.getAccountSiteId(account);
            if (!this.siteId) {
              integStatus.status = 'integrations.MSTeamsToCLPresence.solution-no-mivb';
              return integStatus;
            }
          }
          catch (err) {
            // assuming no siteId
            integStatus.status = 'integrations.MSTeamsToCLPresence.solution-no-mivb';
            return integStatus;
          }
        }
      }

      if (hasMiVB == false) {
        let FFlag = this.featureToggleService.isFeatureEnabled(FeatureFlags.ALLOW_MSTEAMS_ADDITIONAL_PBXES);
        if ( !FFlag )
        {
          integStatus.status = 'integrations.MSTeamsToCLPresence.solution-no-mivb';
          return integStatus;
        }
        let hasPBX = Util.doesAccountHavePBXandGatewayOnboarded(tags, ['MiVoice 5000', 'MX-ONE']);
        if (hasPBX == false)
        {
          integStatus.status = 'integrations.MSTeamsToCLPresence.solution-no-supported-pbx';
          return integStatus;
        }
      }

			let isOn = this.isCheckTagsMSTeamsToCloudLinkIntegrationEnabled(tags);

			if ( isOn === false )
			{
				// before returning a general setup status, check to see if there is a micollab environment
				// if it is, continue, otherwise return a status that is MiCollab related

        if ( !this.isMiCollabConfigured )
        {
          // does not throw an error
          this.isMiCollabConfigured = await this.isMiCollabConfiguredForAccount(account);
          if ( !this.isMiCollabConfigured )
          {
            integStatus.status = 'integrations.MSTeamsToCLPresence.solution-no-micollab';
            return integStatus;
          }
        }

        if ( !this.hasMSTeamsIntegration )
        {
          // does not throw an error
          await this.doesMiCollabHaveMSTeamsIntegration( account );
          if ( !this.hasMSTeamsIntegration )
          {
            integStatus.status = this.msTeamsIntegrationErrorStr;
            return integStatus;
          }
        }

        // if we get here must be a general setup error
        return integStatus;
  		}
			else
			{
        // in case the MS Teams Integration was turned off on the MiCollab, we will want to show that
        if ( !this.hasMSTeamsIntegration )
        {
          // does not throw an error
          await this.doesMiCollabHaveMSTeamsIntegration( account );
          if ( !this.hasMSTeamsIntegration )
          {
            integStatus.status = this.msTeamsIntegrationErrorStr;
            return integStatus;
          }
        }

				// if the feature is enabled, check the health of the monitors
				return this.getAccountMonitorDetailsFromWorkflow(account, tags).then(details => {
					if ( details )
					{
						// if the required number of monitors are more than 1, make sure there are monitors programmed
						// and authorized. 
						if ( details.requiredConnectorIds > 0 )
						{
							if ( tags && tags.MSTeamsToCloudLink && tags.MSTeamsToCloudLink.presenceMonitors )
							{
								// not enough monitors are programmed
								if ( tags.MSTeamsToCloudLink.presenceMonitors.length < details.requiredConnectorIds )
								{
                  integStatus.type = StatusType.error;
									integStatus.status = 'integrations.MSTeamsToCLPresence.more-monitors-required';
									return integStatus;
								}
								else if ( tags.MSTeamsToCloudLink.presenceMonitors.length > 0 )
								{
									// check the status of each monitor
									// first lets get the parent connector
									if ( tags.MSTeams && tags.MSTeams.connectionId )
									{
										return this.workflowService.getMicrosoftConnector(tags.MSTeams.connectionId).then( async msGraphConnector => {
											for ( var i = 0; i < tags.MSTeamsToCloudLink.presenceMonitors.length; i++ )
											{
												try 
												{
													// only need one monitor to not be authorized to show the error
													let status = await this.workflowService.getMicrosoftConnector(tags.MSTeamsToCloudLink.presenceMonitors[i].connectionId).then( result => {
														
														if ( (result.tenantId != msGraphConnector.tenantId) || (result.clientId != msGraphConnector.clientId) )
														{
															// the parent connector was changed
															// this monitor might still be connected BUT it would not be able to be used in feature
															return PresenceMonitorState.NEEDS_UPDATE;
														}
														return result.connected === true ? PresenceMonitorState.AUTHORIZED : PresenceMonitorState.NOT_AUTHORIZED;
													});
													if ( status != PresenceMonitorState.AUTHORIZED )
													{
                            integStatus.type = StatusType.error;
														integStatus.status = 'integrations.MSTeamsToCLPresence.monitors-in-error';
											 			return integStatus;
													}
												}
												catch( err )
												{
													console.error(`${logContext}.getStatusString There was an error retrieving the connector ${tags.MSTeamsToCloudLink.presenceMonitors[i].connectionId} error=`, err);
													integStatus.type = StatusType.error;
                          integStatus.status = 'integrations.MSTeamsToCLPresence.monitors-in-error';
													return integStatus;
												}
											}
											// if we get here, looks like there are no issues
											return undefined;
										})
										.catch( err => {
											// if the graph connector is in error, so are the monitors
											console.error(`${logContext}.getStatusString There was an error retrieving the parent connector ${tags.MSTeams.connectionId} error=`, err);
											integStatus.type = StatusType.error;
                      integStatus.status = 'integrations.MSTeamsToCLPresence.monitors-in-error';
											return integStatus;
										});
									}
									else
									{
										// no graph connector, then the monitors are not valid either
                    integStatus.type = StatusType.error;
										integStatus.status = 'integrations.MSTeamsToCLPresence.monitors-in-error';
										return integStatus;
									}
								}
								// there are enough monitors, now make sure they are all authorized
								// this workflow api is not accurate so will use another method to get the status
								// keeping this around for now. Raised a bug with the api
								// else if ( details.connectorIdStatus && details.connectorIdStatus.length > 0 )
								// {
								// 	for ( var i = 0; i < details.connectorIdStatus.length; i++ )
								// 	{
								// 		if ( details.connectorIdStatus[i].status != PresenceMonitorState.AUTHORIZED )
								// 		{
								// 			integStatus.status = 'integrations.MSTeamsToCLPresence.monitors-in-error';
								// 			return integStatus;
								// 		}
								// 	}
								// }
							}
							// no monitors are programmed, so warn that monitors are required
							else
							{
                integStatus.type = StatusType.error;
								integStatus.status = 'integrations.MSTeamsToCLPresence.more-monitors-required';
								return integStatus;
							}
						}
					}
					else
						return undefined; // is this the best message for this scenario? Will this scenario happen?
				})
        .catch(err2 => {
          // for some reason the WF failed
          console.error(`${logContext}.getStatusString There was an error executing validator WF error=`, err2);
          integStatus.type = StatusType.error;
          integStatus.status = 'integrations.MSTeamsToCLPresence.error-requesting-msteams';
          return integStatus;
        });
			}
		})
  }

  async getCLDSettingsForAccount(account: Account): Promise<any> {
    if (!this.cldSettingsCache.has(account.accountId)) {
      const promise = this.getCLDSettings().then(
        (settings) => {
          return settings;
        },
        (error) => {
          this.clearCLDSettingsCache(account);  // remove rejected Promise from cache
          throw error;
        }
      );
      this.cldSettingsCache.set(account.accountId, promise);
    }
    return this.cldSettingsCache.get(account.accountId);
  }

  clearCLDSettingsCache(account: Account) {
    this.cldSettingsCache.delete(account.accountId);
  }

  getCLDSettings(): Promise<any>
  {
    console.log("In getCLDSettings")
    let cldSettings = undefined;
    return this.systemManagerService.getComponents({ $expand: 'services' }).then( data => {
      console.log("In getCLDSettings", data)
      if ( data )
      {
        let miCollabNetworkElement = Util.getMiCollabFromSysManagerComponentsWithAdminApi(data);
        if ( miCollabNetworkElement )
        {
          cldSettings = {
            miCollabNetworkElement: miCollabNetworkElement
          }
        }
      }
      return cldSettings;
    });
  }

  async isMiCollabAvailable(account: Account): Promise<any> {
    let cldSettings = await this.getCLDSettingsForAccount(account);
    if (cldSettings) { // if CLD environment, cache stores MiCollab Element if applicable 
      return Boolean(cldSettings.miCollabNetworkElement);
    } else {
      this.siteId = await this.getAccountSiteId(account);
      return Boolean(this.siteId);
    }
  }

  async isMiCollabConfiguredForAccount(account: Account): Promise<any> {
    try {
      let cldSettings = await this.getCLDSettingsForAccount(account);
      if (cldSettings) { // if CLD environment, cache stores MiCollab Element if applicable 
        return Boolean(cldSettings.miCollabNetworkElement);
      } else {
        this.siteId = await this.getAccountSiteId(account);
        return await this.micollabService.isMiCollabConfigured(account.accountId, this.siteId);
      }
    } catch (error) {
      console.warn("Error getting setting from MiCollab: ", error);
      return false;
    }
  }

  async doesMiCollabHaveMSTeamsIntegrationWithError(account: Account): Promise<any> {
    try {
      let cldSettings = await this.getCLDSettingsForAccount(account);
      if (cldSettings) {
        let miCollabNetworkElementForCLD = cldSettings.miCollabNetworkElement;
        if (miCollabNetworkElementForCLD && 'componentId' in miCollabNetworkElementForCLD) {
          let componentId = miCollabNetworkElementForCLD['componentId'] as any;
          return await this.micollabService.doesMiCollabHaveMSTeamsIntegrationWithErrorForCLD(componentId);
        } else {
          console.warn(`There's no MiCollab component for account: ${account.accountId}`);
          return false;
        }
      } else {
        this.siteId = await this.getAccountSiteId(account);
        return await this.micollabService.doesMiCollabHaveMSTeamsIntegrationWithError(account.accountId, this.siteId);
      }
    } catch (error) {
      console.warn("Error when calling MiCollab MSTeams Integration With Error: ", error);
      throw(error);
    }
  }

  async enableMiCollabMSTeamsIntegration(account: Account, enable: boolean): Promise<any> {
    try {
      let cldSettings = await this.getCLDSettingsForAccount(account);
      if (cldSettings) {
        let miCollabNetworkElementForCLD = cldSettings.miCollabNetworkElement;
        if (miCollabNetworkElementForCLD && 'componentId' in miCollabNetworkElementForCLD) {
          let componentId = miCollabNetworkElementForCLD['componentId'] as any;
          return await this.micollabService.enableMiCollabMSTeamsIntegrationForCLD(componentId, enable);
        } else {
          console.warn(`There's no MiCollab component for account: ${ account.accountId }`);
          return false;
        }
      } else {
        this.siteId = await this.getAccountSiteId(account);
        return await this.micollabService.enableMiCollabMSTeamsIntegration(account.accountId, this.siteId, enable);
      }
    } catch (error) {
      console.warn("Error enabling MiCollab MSTeams Integration: ", error);
      throw(error);
    }
  }

  async doesAccountHaveMiVoiceBusinessOnboarded(account: Account, accountTags: any): Promise<boolean> {
    let cldSettings = await this.getCLDSettingsForAccount(account);
    if (cldSettings) {
      return true;
    } else {
      return Util.doesAccountHaveMiVoiceBusinessOnboarded(accountTags);
    }
  }

  async getAccountSiteId( account: Account )
  {
    if ( !this.siteId )
    {
      this.siteId = await this.adminService.getSiteIdForAccount( account.accountId );
    }
    return this.siteId;
    // allowing the caller to handle the exception
  }

  async doesMiCollabHaveAConfigurationError( account: Account )
  {
    let hasMSTeams = await this.doesMiCollabHaveMSTeamsIntegration( account );

    if ( hasMSTeams )
      return false;

    // if the MiCollab is in the solution but the integration is OFF return true
    // this will be used to determine that the solution is set up correctly EXCEPT for the 
    // MSTeams Integration flag being off. Since the accounts app will be turning this flag on
    // this is no longer a bad result
    if ( (this.msTeamsIntegrationErrorStr == undefined) || 
        (this.msTeamsIntegrationErrorStr == 'integrations.MSTeamsToCLPresence.solution-msteams-disabled') )
    {
      return false;
    }
    return true; // there is an error that indicates a configuration error
  }

  // requesting this information seems to take awhile and not likely to be changing
  // Martin G stated the user might decide to turn it on and off if they don't like the feature
  // no idea how valid this use case is. For now it is saving a lot of time so
  // we will cache the value. If this use case becomes a problem we will have to remove this caching
  async doesMiCollabHaveMSTeamsIntegration( account: Account )
  {
    if ( (!this.hasMSTeamsIntegration) || (this.msTeamsIntegrationErrorStr != undefined) ) // we are caching a good value, and retesting a bad one
    {
      try
      {
        this.hasMSTeamsIntegration = await this.doesMiCollabHaveMSTeamsIntegrationWithError( account );
        this.msTeamsIntegrationErrorStr = undefined;
        if ( !this.hasMSTeamsIntegration )
          this.msTeamsIntegrationErrorStr = 'integrations.MSTeamsToCLPresence.solution-msteams-disabled';
      }
      catch(err)
      {
        this.hasMSTeamsIntegration = false;
        this.msTeamsIntegrationErrorStr = undefined;
        if ( err.statusCode == 404 )
        {
          this.msTeamsIntegrationErrorStr = 'integrations.MSTeamsToCLPresence.solution-no-micollab';
        }
        else if ( err.statusCode == 403 )
        {
          this.msTeamsIntegrationErrorStr = 'integrations.MSTeamsToCLPresence.no-msteams-error';
        }
        else if ( err.statusCode >= 500 )
        {
          this.msTeamsIntegrationErrorStr = 'integrations.MSTeamsToCLPresence.error-requesting-msteams';
        }
      }
      return this.hasMSTeamsIntegration;
    }
    return Promise.resolve(this.hasMSTeamsIntegration);
    // does not throw an error
  }

  getAccountMonitorDetailsFromWorkflow(account: Account, tags: any, reset: boolean = false): Promise<any>
	{
    let monitorList = [];
    let workflowId;	
    let msConnectorId;
    let receiverWorkerId;
    let adjustedReset = false;

    if ( tags && tags.MSTeams )
    {
      msConnectorId = tags.MSTeams?.connectionId;
    }

    if ( msConnectorId == undefined )
    {
      console.error(`${logContext}.getAccountMonitorDetailsFromWorkflow Error getting account monitor details - msConnectorId is undefined`);
      throw new Error("Error getting account monitor details - msConnectorId is undefined");
    }

    if ( tags && tags.MSTeamsToCloudLink )
    {
      if ( tags.MSTeamsToCloudLink.presenceMonitors )
      {
        console.debug(`${logContext}.getAccountMonitorDetailsFromWorkflow integ ms teams, monitor list: `, tags.MSTeamsToCloudLink.presenceMonitors);
        monitorList = this.tagMonitorListToConnectorList(tags.MSTeamsToCloudLink.presenceMonitors);
      }
      workflowId = tags.MSTeamsToCloudLink.validatorWorkflow?.workflowId; 
      if ( workflowId == undefined )
      {
        console.error(`${logContext}.getAccountMonitorDetailsFromWorkflow Error getting account monitor details - workflowId is undefined`);
        throw new Error("Error getting account monitor details - workflowId is undefined");
      }

      if ( (reset == true) && (tags.MSTeamsToCloudLink.enabled == true) )
      {
        // a reset can only happen when bi-sync is enabled
        receiverWorkerId = tags.MSTeamsToCloudLink.msGraphPresenceChangeWorkflow?.workflowId
        if ( receiverWorkerId == undefined )
        {
          console.error(`${logContext}.getAccountMonitorDetailsFromWorkflow Error getting account monitor details - receiverWorkerId is undefined`);
          throw new Error("Error getting account monitor details - receiverWorkerId is undefined");
        }
        adjustedReset = true;
      }

      return this.getMonitorWorkflowStatus(workflowId, monitorList, msConnectorId, adjustedReset, receiverWorkerId).then(result => {
        console.debug(`${logContext}.getAccountMonitorDetailsFromWorkflow Successfully executed validator workflow result=`, result);
        // do not trust the returned information so I will not delete connectors based on it
        // if ( result && result.removedConnectors && result.removedConnectors.length > 0 )
        // {
        // 	// it was detected that we have one or more monitors that have been removed from azure
        // 	// there is no way the monitor can be saved so we will simply remove it
        // 	// this is not a typical use case so we are removing the connector one at a time for now
        // 	result.removedConnectors.forEach( async removedConnector => {
        // 		await this.deleteConnectorData(account, removedConnector);
        // 	});
        // }
        return result;
      })
      .catch(err => {
        console.error(`${logContext}.getAccountMonitorDetailsFromWorkflow Error getting account monitor details err= `, err);
        throw err;
      })
    }
    else
      return Promise.resolve({}); // never should happen
	}

  isCheckTagsMSTeamsToCloudLinkIntegrationSetup( tags: any ): boolean
	{
		if ( tags && tags.MSTeamsToCloudLink )
		{
			if ( tags.MSTeamsToCloudLink.setupComplete == true )
				return true;
			return false;
		}
		else
			return false;
	}

  isCheckTagsMSTeamsToCloudLinkIntegrationEnabled( tags: any ): boolean
	{
		if ( tags && tags.MSTeamsToCloudLink )
		{
			if ( tags.MSTeamsToCloudLink.enabled == true )
				return true;
			return false;
		}
		else
			return false;
	}

  isCheckTagsCloudLinkToMSTeamsIntegrationEnabled( tags: any ): boolean
	{
    if ( tags && tags.MSTeamsPresence )
    {
      if ( tags.MSTeamsPresence.enabled == true )
        return true;
      return false;
    }
    else
      return false;
	}

  tagMonitorListToConnectorList(monitorList: any[]): string[]
	{
		let connectorList = [];
    
    if (!monitorList )
      return connectorList;

		for ( let monitor of monitorList )
		{
			connectorList.push(monitor.connectionId);
		}
		return connectorList;
	}

  isMainConnectorClientSecretExpired(connectorData: any): Promise<boolean> {
    if ( this.clientSecretConnectorData &&
      ((connectorData.connectionId != this.clientSecretConnectorData.connectionId) ||
        (connectorData.name != this.clientSecretConnectorData.name) ||
        (connectorData.clientId != this.clientSecretConnectorData.clientId)) ) 
    {
      this.clearClientSecretCache(); // something changed about the connector so reset the cache
    }
    if ( !this.clientSecretExpiry ) 
    {
      // we have the expiry, use it
      // TBD we will need a trigger where we clear the cache in the
      // case that the user goes to azure and makes changes. We will not know
      // so there should be a point that the client calling this will need 
      // to know the optimal time to clear the cache

      let request: WorkerPostRequest = {
        inputs: {
          ConnectionId: connectorData.connectionId,
          ApplicationId: connectorData.clientId
        }
      }

      return this.workflowService.executeSyncWorkflow( MSTeamsWorkflows.clientSecretWorkflow.flowId, request ).then( secretData => {
        console.log(`${logContext}.isMainConnectorClientSecretExpired This is the returned data of the client secret`, secretData);
        if ( secretData && secretData.length >= 1 ) 
        {
          // we are trying to determine if the client secret for the connector has expired. We can only match it if
          // the name of the connector is equal to the name of the client secret
          let matched = secretData.filter(data => {
            return data.name == connectorData.name;
          });

          // this is really overkill as the WF would fail if expired
          if ( matched && matched.length == 1 ) 
          {
            let expiry = new Date(matched[0].expiry).getTime();
            let now = new Date().getTime();
            this.clientSecretConnectorData = connectorData;
            this.clientSecretExpiry = expiry;

            if ( expiry < now ) 
            {
              return true;
            }
            return false;
          }
        }
        return false;
      })
      .catch(err => {
        console.error(`${logContext}.isMainConnectorClientSecretExpired Error getting the client secret date= `, err);
        // if the WF fails, there could be something wrong, but we may not know what
        // last time we checked this meant the client secret expired
        // not guaranteed that is always the case
        if (err?.statusCode == 401 &&
          err?.body?.message?.error?.code == 'InvalidAuthenticationToken' &&
          err?.body?.message?.error?.message?.includes('the token is expired')) {
          // the client secret is expired
          return true;
        }

        throw(err); // there is an error that could mean the connector is not setup properly, let the caller decide how to handle the error
      });
    }
    else 
    {
      let now = new Date().getTime();

      if ( this.clientSecretExpiry < now ) 
      {
        return Promise.resolve(true);
      }
      return Promise.resolve(false);
    }
  }


  // since this is being called in one place right after the method above we
  // will assume that we have a client secret to work with or return false
  // if these conditions changes, rethink this
  isMainConnectorClientSecretExpiring(): boolean 
  {
    let EXPIRING_GAP = 30 * 24 * 60 * 60 * 1000;  // 30 days in milliseconds

    if ( this.clientSecretExpiry ) 
    {
      let now = new Date().getTime();
      let difference = this.clientSecretExpiry - now;
      if ( difference < EXPIRING_GAP )
        return true;
      return false;
    }
    else 
    {
      return false;
    }
  }
  // since this is being called in one place right after the method above we
  // will assume that we have a client secret to work with or return false
  // if these conditions changes, rethink this
  getMainConnectorClientSecretAsFormattedDate()
  {
    if ( !this.clientSecretExpiry )
      return undefined; // not likely called if there is no expired secret

    return new Date(this.clientSecretExpiry).toLocaleDateString(); // this might need to change
  }

  checkForMissingWFGraphPermission(err)
  {
    if (err?.statusCode == 403 &&
      err?.body?.message?.error?.code == 'Authorization_RequestDenied' &&
      err?.body?.message?.error?.message?.includes('Insufficient privileges')) {
      // the azure app does not have to permissions to request the client secret status
      return true;
    }
    return false; // we do not know if there are other variations of this error ATM
  }

  confirmPresenceFeatureStateChange(account: Account, state: boolean): Promise<boolean>
  {
    // ATM turning on the subfeature is fine
    if ( state == true)
      return Promise.resolve(true);

    // the feature is only concerned with the toggle being turned off if the ms teams to cloudlink feature is ON
    // get the account tags
    return this.adminService.getAccountTags( account.accountId ).then( tags => {
      // if the integration has never been setup or enabled there is nothing to do
      if ( tags && tags.MSTeamsToCloudLink && tags.MSTeamsToCloudLink.setupComplete == true && tags.MSTeamsToCloudLink.enabled == true )
      {
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, { data: { mode: ConfirmationDialogs.DISABLE_MSTEAMS_TO_CLOUDLINK_CONFIRMATION } });
        return dialogRef.afterClosed().toPromise().then(result => {
          if ( result == undefined )
            return false;
          else
            return true;
        });
      }
      else
      {
        // the feature is not enabled so we can turn it off
        return Promise.resolve(true);
      }
    });
  }

  isMSTeamsUpgradePending( account: Account ): Promise<boolean>
	{
		if ( !this.upgradePendingCache.has(account.accountId) )
		{
      // also request if there is a webapp update pending. It is on a 5 minute update. Let force a refresh as it might affect
      // how a MSTeams update is handled
      this.pwaService.checkForUpdate();

      let request: WorkerPostRequest = {}
			const promise = this.workflowService.executeSyncWorkflow( MSTeamsWorkflows.featureUpdatePending.flowId, request )
				.then(
					(result) => {
            console.log(`${logContext}.isMSTeamsUpgradePending Output returned from WF `, result);
            if ( result && result.upgradeRequired != undefined)
            {
              return result.upgradeRequired;
            }
            else
            {
              this.clearUpgradePendingCache( account );
              return false;
            }
          },
					(error) => {
						this.clearUpgradePendingCache( account );  // remove rejected Promise from cache
						throw error;
					}
				);
			this.upgradePendingCache.set(account.accountId, promise);
		}
		return this.upgradePendingCache.get( account.accountId );
	}

  clearUpgradePendingCache( account: Account )
  {
    this.upgradePendingCache.delete( account.accountId );
  }

  async removeCloudLinkToMSTeamsWorkflow( account: Account, tags: any )
  {
    if ( tags && tags.MSTeamsPresence && tags.MSTeamsPresence.activityId )
    {
      // everything was setup so delete it all
      try
      {
        await this.workflowService.deleteMSTeamsPresenceWorkflowAndAll(
          tags.MSTeamsPresence.activityId,
          tags.MSTeamsPresence.clientId,
          tags.MSTeamsPresence.policyId,
          account.accountId);
      }
      catch (e)
      {
        // delete is always best effort so it would not be a failure if something did not delete 
        // properly
        console.error(`${logContext}.removeCloudLinkToMSTeamsWorkflow  error when removing the CloudLink to MS Teams presence workflow, continuing`, e);
      }
    }
  }

  async removeMSTeamsToCloudLinkWorkflows( account: Account, tags: any )
  {
    // remove the presence workflows
    // basically need to undo all this
    // tags.MSTeamsToCloudLink: {
    //	client: {
    //		clientId: "clientId",
    //		policyId: "policyId"
    //	},
    // 	validatorWorkflow: {
    // 		enabled: true,
    // 		workflowId: "workflowId",
    // 		workflowVersion: "version"		
    // 	},
    // 	msGraphPresenceChangeWorkflow: {
    // 		enabled: false
    // 		workflowId: "workflowId",
    // 		workflowVersion: "version"		
    // 	},
    // 	msTeamsPresenceToPBXUserChangeWorkflow: {
    // 		enabled: false
    // 		workflowId: "workflowId",
    // 		workflowVersion: "version"		
    // 	},
    // 	msTeamsPresenceToPBXSchedulerWorkflow: {
    // 		enabled: false
    // 		workflowId: "workflowId",
    // 		workflowVersion: "version"		
    // 	},
    // 	updateMiCollabPresenceWorkflow: {
    // 		enabled: false
    // 		workflowId: "workflowId",
    // 		workflowVersion: "version"		
    // 	},
    //	setupComplete: true or false

    if ( tags && tags.MSTeamsToCloudLink && tags.MSTeamsToCloudLink.client )
    {
      // deleting of the workflow will be best effort
      if ( tags.MSTeamsToCloudLink.validatorWorkflow != undefined )
      {
        try
        {
          await this.workflowService.deleteWorkflow( tags.MSTeamsToCloudLink.validatorWorkflow.workflowId);
        }
        catch (err)
        {
          console.error(`${logContext}.removeMSTeamsToCloudLinkWorkflows delete of validatorWorkflow failed, continuing with the rest of the deleting error=`, err);
        }
      }

      if ( tags.MSTeamsToCloudLink.msGraphPresenceChangeWorkflow != undefined )
      {
        try
        {
          await this.workflowService.deleteWorkflow( tags.MSTeamsToCloudLink.msGraphPresenceChangeWorkflow.workflowId);
        }
        catch (err)
        {
          console.error(`${logContext}.removeMSTeamsToCloudLinkWorkflows delete of msGraphPresenceChangeWorkflow failed, continuing with the rest of the deleting error=`, err);
        }
      }

      if ( tags.MSTeamsToCloudLink.msTeamsPresenceToPBXUserChangeWorkflow != undefined )
      {
        try
        {
          await this.workflowService.deleteWorkflow( tags.MSTeamsToCloudLink.msTeamsPresenceToPBXUserChangeWorkflow.workflowId);
        }
        catch (err)
        {
          console.error(`${logContext}.removeMSTeamsToCloudLinkWorkflows delete of msTeamsPresenceToPBXUserChangeWorkflow failed, continuing with the rest of the deleting error=`, err);
        }
      }

      if ( tags.MSTeamsToCloudLink.msTeamsPresenceToPBXSchedulerWorkflow != undefined )
      {
        try
        {
          await this.workflowService.deleteWorkflow( tags.MSTeamsToCloudLink.msTeamsPresenceToPBXSchedulerWorkflow.workflowId);
        }
        catch (err)
        {
          console.error(`${logContext}.removeMSTeamsToCloudLinkWorkflows delete of msTeamsPresenceToPBXSchedulerWorkflow failed, continuing with the rest of the deleting error=`, err);
        }
      }

      if ( tags.MSTeamsToCloudLink.updateMiCollabPresenceWorkflow != undefined )
      {
        try
        {
          await this.workflowService.deleteWorkflow( tags.MSTeamsToCloudLink.updateMiCollabPresenceWorkflow.workflowId);
        }
        catch (err)
        {
          console.error(`${logContext}.removeMSTeamsToCloudLinkWorkflows delete of updateMiCollabPresenceWorkflow failed, continuing with the rest of the deleting error=`, err);
        }
      }

      try {
        await this.adminService.deleteClientById( tags.MSTeamsToCloudLink.client.clientId );
      }
      catch (e)
      {
        console.error(`${logContext}.removeMSTeamsToCloudLinkWorkflows The deletion of the client failed, continuing with the rest of the deleting error=`, e);
      }
  
      try {
        await this.adminService.deletePolicyById( account.accountId, tags.MSTeamsToCloudLink.client.policyId );
      }
      catch (e)
      {
        console.error(`${logContext}.removeMSTeamsToCloudLinkWorkflows The deletion of the client policy failed, continuing with the rest of the deleting error=`, e);
      }
    }
  }

  // creating a separate method for this might be overkill but it is at least consistent
  // I am expecting changes in the future
  createCloudLinkToMSTeamsWorkflow( account: Account, tags: any ): Promise<any>
  {
    return this.workflowService.createMSTeamsPresenceWorkflow( tags.MSTeams.connectionId, account.accountId ).then( workflowData => {					

			let dataChanges = {
				tags: { 
					MSTeamsPresence: {
						enabled: true,
						activityId: workflowData.workflowId,
						version: workflowData.workflowVersion,
						clientId: workflowData.clientId,
						policyId: workflowData.policyId
					}
				}
			};
      return dataChanges;
    })
    .catch( err => {
      console.error(`${logContext}.createCloudLinkToMSTeamWorkflow Error setting up the workflow error=`, err);

      throw err;
    })  
  }

  createMSTeamsToCloudLinkWorkflows( account: Account, tags: any ): Promise<any>
  {
    let msGraphConnectorId;

    if ( tags && tags.MSTeams && tags.MSTeams.setupComplete == true && tags.MSTeams.connectionId != undefined )
    {
      msGraphConnectorId = tags.MSTeams.connectionId;
    }
    else
    {
      let err = new Error("MS Teams integration not setup");
      throw err;
    }

    // create the client
    return this.workflowService.createClientAndClientOptions(account.accountId).then( clientData => {
      // MiCollab Presence Status WF
      return this.workflowService.createMiCollabPresenceStatusWorkflow( clientData.client, clientData.options).then( miCollabResult => {
        // MS Graph Presence Change WF
        return this.workflowService.createMSGraphPresenceChangeWorkflow(msGraphConnectorId, clientData.client, clientData.options, miCollabResult.workflowId).then( msGraphResult => {
          // MS Teams presence to Mitel PBX User Change WF
          return this.workflowService.createMSTeamsPresenceToPBXGenericWorkflow(MSTeamsWorkflows.msTeamsPresenceToMitelPBXUserChange, msGraphResult.workflowId, clientData.client, clientData.options).then( userChangeResult => {
            // MS Teams presence to Mitel PBX Scheduler
            return this.workflowService.createMSTeamsPresenceToPBXSchedulerWorkflow(MSTeamsWorkflows.msTeamsPresenceToMitelPBXScheduler, msGraphResult.workflowId, clientData.client, clientData.options).then( schedulerResult => {
              // MS Teams presence to Mitel PBX Validate Connection (should be enabled)
              return this.workflowService.createMSTeamsConnectorValidatorWorkflow(clientData.client, clientData.options).then( validatorResult => {

                // Save the WF data to the accounts tags
                // this will be the format
                // tags.MSTeamsToCloudLink: {
                // 	presenceMonitors: [
                // 		{monitorId: "monitorId", connectionId: "connectionId"}
                // 	],
                //	client: {
                //		clientId: "clientId",
                //		policyId: "policyId"
                //	},
                // 	validatorWorkflow: {
                // 		enabled: true,
                // 		workflowId: "workflowId",
                // 		workflowVersion: "version"		
                // 	},
                // 	msGraphPresenceChangeWorkflow: {
                // 		enabled: false
                // 		workflowId: "workflowId",
                // 		workflowVersion: "version"		
                // 	},
                // 	msTeamsPresenceToPBXUserChangeWorkflow: {
                // 		enabled: false
                // 		workflowId: "workflowId",
                // 		workflowVersion: "version"		
                // 	},
                // 	msTeamsPresenceToPBXSchedulerWorkflow: {
                // 		enabled: false
                // 		workflowId: "workflowId",
                // 		workflowVersion: "version"		
                // 	},
                // 	updateMiCollabPresenceWorkflow: {
                // 		enabled: false
                // 		workflowId: "workflowId",
                // 		workflowVersion: "version"		
                // 	},
                //	setupComplete: true or false
                
                let dataChanges = {
                  tags: { 
                    MSTeamsToCloudLink: {
                      client: {
                        clientId: clientData.client.clientId,
                        policyId: clientData.client.policyId
                      },
                      validatorWorkflow: {
                        enabled: true,
                        workflowId: validatorResult.workflowId,
                        workflowVersion: validatorResult.workflowVersion	
                      },
                      msGraphPresenceChangeWorkflow: {
                        enabled: false,
                        workflowId: msGraphResult.workflowId,
                        workflowVersion: msGraphResult.workflowVersion
                      },
                      msTeamsPresenceToPBXUserChangeWorkflow: {
                        enabled: false,
                        workflowId: userChangeResult.workflowId,
                        workflowVersion: userChangeResult.workflowVersion
                      },
                      msTeamsPresenceToPBXSchedulerWorkflow: {
                        enabled: false,
                        workflowId: schedulerResult.workflowId,
                        workflowVersion: schedulerResult.workflowVersion
                      },
                      updateMiCollabPresenceWorkflow: {
                        enabled: false,
                        workflowId: miCollabResult.workflowId,
                        workflowVersion: miCollabResult.workflowVersion
                      }
                    }
                  }
                };  
                return dataChanges;
              });
            });
          });
        });
      });
    })
    .catch( err => {
      console.error(`${logContext}.createMSTeamsToCloudLinkWorkflows Error setting up all workflows error=`, err);

      throw err;
    })  
  }

  restorePreviousCloudLinkToMSTeamsWorkflowState(account: Account, tags: any, workflowData: any): Promise<any>
  {
    // only restore if the WFs were created
    if ( workflowData == null )
      return Promise.resolve(true);  // the value does not really mean anything at this point

    workflowData.tags.MSTeamsPresence.enabled = tags.MSTeamsPresence.enabled;

    // update the account tags
    return this.adminService.updateAccountWithoutSpinner( account.accountId, workflowData).then(result => {
      this.accountService.mergeAccountWithModel(result);
      console.debug(`${logContext}.restorePreviousCloudLinkToMSTeamsWorkflowState restored workflow result = `, result);
      
      // disable the WF if the subfeature is off note: this one is created on by default
      if ( tags.MSTeamsPresence.enabled == false )
      {
        return this.adminService.getAccountWithoutFrills(account.accountId, null, {query: {$expand: 'tags'}}).then( async newAccount => {
          return this.enableCloudLinkToMSTeamsPresenceWorkflow( newAccount, false).then( retVal => {
            return true;
          });
        });
      }
      else
      {
        return true;
      }
    })
  }
  
  restorePreviousMSTeamsToCloudLinkWorkflowState(account: Account, tags: any, workflowData: any): Promise<any> 
  {
    // only restore if the WFs were created
    if ( workflowData == null )
      return Promise.resolve(true); // the value does not really mean anything at this point

    // program any existing presence monitors into the WFs that required them
    let connectorList = this.tagMonitorListToConnectorList(tags.MSTeamsToCloudLink.presenceMonitors);
    return this.workflowService.configureWorkflowConnectorIdVariable(workflowData.tags.MSTeamsToCloudLink.msTeamsPresenceToPBXUserChangeWorkflow.workflowId,
        connectorList).then( result1 => {
      return this.workflowService.configureWorkflowConnectorIdVariable(workflowData.tags.MSTeamsToCloudLink.msTeamsPresenceToPBXSchedulerWorkflow.workflowId,
            connectorList).then( result2 => {

        // Take this opportunity to save the account tags with the new WF/ client information
        workflowData.tags.MSTeamsToCloudLink.presenceMonitors = tags.MSTeamsToCloudLink.presenceMonitors;
        workflowData.tags.MSTeamsToCloudLink.setupComplete = tags.MSTeamsToCloudLink.setupComplete;
        workflowData.tags.MSTeamsToCloudLink.enabled = tags.MSTeamsToCloudLink.enabled;

        return this.adminService.updateAccountWithoutSpinner( account.accountId, workflowData).then(result => {
          this.accountService.mergeAccountWithModel(result);
          console.debug(`${logContext}.restorePreviousMSTeamsToCloudLinkWorkflowState restored all workflows result = `, result);
          
          // enable the WFs if the bidirection subfeature is on
          if ( tags.MSTeamsToCloudLink.enabled == true )
          {
            return this.adminService.getAccountWithoutFrills(account.accountId, null, {query: {$expand: 'tags'}}).then( async newAccount => {
              return this.enableMSTeamsToCloudLinkPresenceWorkflows( newAccount, true).then( retVal => {
                return true;
              });
            });
          }
          else
          {
            return true;
          }
        })
      });
    })
    .catch( err => {
      console.error(`${logContext}.restorePreviousMSTeamsToCloudLinkWorkflowState Error setting up all workflows error=`, err);
      throw err;
    })
  }

  enableMSTeamsToCloudLinkPresenceWorkflows( account: Account, enable: boolean ): Promise<any>
	{
		let enableParam: string = "0.0.0";
		// update the enabled state
		let dataChanges = {
			tags: account.tags
		};

		// there are 4 workflows to enable (or disabled)
		// 	msGraphPresenceChangeWorkflow: {
		// 		enabled: false
		// 		workflowId: "workflowId",
		// 		workflowVersion: "version"		
		// 	},
		// 	msTeamsPresenceToPBXUserChangeWorkflow: {
		// 		enabled: false
		// 		workflowId: "workflowId",
		// 		workflowVersion: "version"		
		// 	},
		// 	msTeamsPresenceToPBXSchedulerWorkflow: {
		// 		enabled: false
		// 		workflowId: "workflowId",
		// 		workflowVersion: "version"		
		// 	},
		// 	updateMiCollabPresenceWorkflow: {
		// 		enabled: false
		// 		workflowId: "workflowId",
		// 		workflowVersion: "version"		
		// 	},

		if ( account.tags.MSTeamsToCloudLink.msGraphPresenceChangeWorkflow &&
			account.tags.MSTeamsToCloudLink.msTeamsPresenceToPBXUserChangeWorkflow &&
			account.tags.MSTeamsToCloudLink.msTeamsPresenceToPBXSchedulerWorkflow &&
			account.tags.MSTeamsToCloudLink.updateMiCollabPresenceWorkflow )
		{
			// all the workflows should exist if these tags exist
			if ( account.tags.MSTeamsToCloudLink.msGraphPresenceChangeWorkflow && enable == true )
			{
				enableParam = account.tags.MSTeamsToCloudLink.msGraphPresenceChangeWorkflow.workflowVersion;
			}
		
			return this.workflowService.enableWorkflow(
				account.tags.MSTeamsToCloudLink.msGraphPresenceChangeWorkflow.workflowId, enableParam).then( result => {
					dataChanges.tags.MSTeamsToCloudLink.msGraphPresenceChangeWorkflow.enabled = enable;

					if ( account.tags.MSTeamsToCloudLink.msTeamsPresenceToPBXUserChangeWorkflow && enable == true )
					{
						enableParam = account.tags.MSTeamsToCloudLink.msTeamsPresenceToPBXUserChangeWorkflow.workflowVersion;
					}
					return this.workflowService.enableWorkflow(
						account.tags.MSTeamsToCloudLink.msTeamsPresenceToPBXUserChangeWorkflow.workflowId, enableParam).then( result => {
							dataChanges.tags.MSTeamsToCloudLink.msTeamsPresenceToPBXUserChangeWorkflow.enabled = enable;

							if ( account.tags.MSTeamsToCloudLink.msTeamsPresenceToPBXSchedulerWorkflow && enable == true )
							{
								enableParam = account.tags.MSTeamsToCloudLink.msTeamsPresenceToPBXSchedulerWorkflow.workflowVersion;
							}
							return this.workflowService.enableWorkflow(
								account.tags.MSTeamsToCloudLink.msTeamsPresenceToPBXSchedulerWorkflow.workflowId, enableParam).then( result => {
									dataChanges.tags.MSTeamsToCloudLink.msTeamsPresenceToPBXSchedulerWorkflow.enabled = enable;

									if ( account.tags.MSTeamsToCloudLink.updateMiCollabPresenceWorkflow && enable == true )
									{
										enableParam = account.tags.MSTeamsToCloudLink.updateMiCollabPresenceWorkflow.workflowVersion;
									}
									return this.workflowService.enableWorkflow(
										account.tags.MSTeamsToCloudLink.updateMiCollabPresenceWorkflow.workflowId, enableParam).then( result => {
											dataChanges.tags.MSTeamsToCloudLink.updateMiCollabPresenceWorkflow.enabled = enable;

											// save the updated tags
											dataChanges.tags.MSTeamsToCloudLink.enabled = enable;
											return this.adminService.updateAccountWithoutSpinner( account.accountId, dataChanges).then(result => {
												this.accountService.mergeAccountWithModel(result);
												return true;
											});
									});
							});
					});
			})
			.catch (err => {
				console.error(`${logContext}.enableMSTeamsToCloudLinkPresenceWorkflows Is this an error to be concerned about? error=`, err);
				throw err;
			});
		}
		else
		{
			console.error( `${logContext}.enableMSTeamsToCloudLinkPresenceWorkflows This is an error that should not exist. There are missing MS Teams workflows.`);
			return Promise.resolve(false);
		}
	}

	enableCloudLinkToMSTeamsPresenceWorkflow( account: Account, enable: boolean ): Promise<any>
	{
		let enableParam: string = "0.0.0";
		if ( enable == true )
			enableParam = account.tags.MSTeamsPresence.version;

		return this.workflowService.enableWorkflow(
			account.tags.MSTeamsPresence.activityId, enableParam).then( result => {
				// update the enabled state
				let dataChanges = {
					tags: account.tags
				};
				dataChanges.tags.MSTeamsPresence.enabled = enable;
				return this.adminService.updateAccountWithoutSpinner( account.accountId, dataChanges).then(result => {
					this.accountService.mergeAccountWithModel(result);
					return true;
				});
			})
			.catch (err => {
				console.error(`${logContext}.enableCloudLinkToMSTeamsPresenceWorkflow Is this an error to be concerned about? error=`, err);
				throw err;
			})
	}

}
